// Student Desk — the single unified MyDeskey home page.
//
// This page merges what used to live on /student/institution into one
// cohesive desk so multi-institution students see EVERYTHING for their
// currently-active school in a single scroll:
//
//   1. Greeting + active institution chip
//   2. Active institution hero (per-institution credits + super credits)
//   4. Pending invitations (Accept / Decline)
//   5. My Institutions grid (per-institution credits + Switch Here)
//   6. My Classes (active institution)
//   7. Recently Published lessons (active institution)
//
// Every data block re-fetches whenever activeTid flips, so a student in
// multiple schools sees a completely different desk per institution —
// different classes, lessons, and credits.
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import api from './studentApi';
import { useStudent } from './StudentApp';
import { toast } from 'sonner';
import {
  ArrowRight, Building2, GraduationCap, Sparkles, Mail, Inbox, Check, X,
  BookOpen, Calendar, Layers, ShieldCheck,
} from 'lucide-react';

// Picks readable text (white or dark ink) for a colored surface — used on
// the Active Institution hero, whose background is the STUDENT's own theme
// gradient (primary → accent). A plain hardcoded white reads fine on most
// themes' primary color, but a handful of pastel/light themes (e.g. Cotton
// Cloud, Nordic Frost) have a light primary where white text loses contrast.
function relativeLuminance(hex) {
  const c = (hex || '').replace('#', '');
  if (c.length !== 6) return 0;
  const r = parseInt(c.slice(0, 2), 16) / 255;
  const g = parseInt(c.slice(2, 4), 16) / 255;
  const b = parseInt(c.slice(4, 6), 16) / 255;
  const lin = (v) => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4));
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}
function pickContrastColor(hex, dark = '#0F172A', light = '#FFFFFF') {
  if (!hex) return light;
  return relativeLuminance(hex) > 0.5 ? dark : light;
}

export default function StudentDeskPage() {
  const nav = useNavigate();
  const { me, memberships, activeTid, activeMem, refreshMe, switchInstitution, theme } = useStudent();
  const [overview, setOverview] = useState(null);       // /student/active-institution/overview
  const [hub, setHub] = useState(null);                 // /student/active-institutions/list
  const [recos, setRecos] = useState([]);
  const [requests, setRequests] = useState([]);         // legacy /auth/my-join-requests (back-compat)
  const [busyId, setBusyId] = useState(null);
  const [switching, setSwitching] = useState(null);

  // ── Refetch everything whenever the active institution changes ───────────
  const loadAll = useCallback(async () => {
    try {
      const [ov, hubData, recList, jrLegacy] = await Promise.all([
        activeTid ? api.get('/student/active-institution/overview').catch(() => null) : Promise.resolve(null),
        api.get('/student/active-institutions/list').catch(() => null),
        api.get('/student/recommendations').catch(() => ({ recommendations: [] })),
        api.get('/auth/my-join-requests').catch(() => ({ join_requests: [] })),
      ]);
      setOverview(ov);
      setHub(hubData);
      setRecos(recList.recommendations || []);
      setRequests(jrLegacy.join_requests || []);
    } catch { /* silent — partial loads are okay */ }
  }, [activeTid]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Switch institution from a card click ─────────────────────────────────
  const switchTo = useCallback(async (tid) => {
    if (tid === activeTid || switching) return;
    setSwitching(tid);
    try {
      await switchInstitution(tid);
      // switchInstitution already calls refreshMe + navs to /student/desk;
      // loadAll() will refire because activeTid changes.
    } finally {
      setSwitching(null);
    }
  }, [activeTid, switching, switchInstitution]);

  // ── Accept / decline a pending invite (both shapes) ──────────────────────
  const acceptInvite = useCallback(async (jrId, instName) => {
    setBusyId(jrId);
    try {
      await api.post('/student/invites/accept', { join_request_id: jrId });
      toast.success(`Joined ${instName || 'the institution'}`);
      await refreshMe();
      await loadAll();
    } catch (e) {
      toast.error(e.message || 'Could not accept invite');
    } finally { setBusyId(null); }
  }, [refreshMe, loadAll]);

  const declineInvite = useCallback(async (jrId) => {
    setBusyId(jrId);
    try {
      await api.post('/student/invites/decline', { join_request_id: jrId });
      toast.success('Invite declined');
      await loadAll();
    } catch (e) {
      toast.error(e.message || 'Could not decline invite');
    } finally { setBusyId(null); }
  }, [loadAll]);

  // Legacy join-requests endpoint (kept for back-compat with old admin invites)
  const respondLegacy = async (id, action) => {
    setBusyId(id);
    try {
      await api.post(`/auth/join-request/${id}/respond`, { action });
      toast.success(action === 'accept' ? 'Joined institution!' : 'Request declined');
      await refreshMe();
      await loadAll();
    } catch (e) {
      toast.error(e.message || 'Failed');
    } finally { setBusyId(null); }
  };

  // ── Derived state ────────────────────────────────────────────────────────
  const profile = me?.student_profile || {};
  const greet = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  })();
  const classes = overview?.classes || [];
  const recentTopics = overview?.recent_topics || [];
  const tenant = overview?.tenant;
  const credits = overview?.credits;
  const superCredits = hub?.super_credits ?? me?.super_credits ?? 0;
  const pendingJoinRequests = hub?.pending_join_requests || [];
  const pendingInvitesPre = hub?.pending_invites || [];
  const pendingTotal = pendingJoinRequests.length + pendingInvitesPre.length + requests.length;

  return (
    <div className="space-y-8" data-testid="student-desk">

      {/* ── 1) Greeting — elegant, theme-aware, with editorial accent ───── */}
      <section data-testid="student-desk-greeting">
        <div className="flex flex-wrap items-end justify-between gap-4 relative">
          {/* Subtle decorative gradient orb behind the greeting (uses the
              active theme's primary + accent so it changes per theme). */}
          <div
            aria-hidden="true"
            className="absolute -top-8 -left-6 w-40 h-40 rounded-full opacity-[0.08] blur-2xl pointer-events-none"
            style={{ background: 'radial-gradient(circle, var(--st-primary), transparent 70%)' }}
          />
          <div className="relative">
            <p
              className="text-[10px] font-bold uppercase tracking-[0.22em] flex items-center gap-2"
              style={{ color: 'var(--st-mute)' }}>
              <span className="inline-block w-6 h-px" style={{ background: 'var(--st-primary)' }} />
              {greet}
              <span className="opacity-50">·</span>
              <span className="opacity-80" style={{ color: 'var(--st-ink)' }}>
                {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}
              </span>
            </p>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-2 leading-[1.05]"
              style={{
                fontFamily: 'var(--st-font-heading)',
                color: 'var(--st-ink)',
                letterSpacing: '-0.025em',
              }}>
              Hi,{' '}
              <span
                className="bg-clip-text"
                style={{
                  backgroundImage: 'linear-gradient(135deg, var(--st-primary) 0%, var(--st-accent) 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>
                {(me.full_name || 'Student').split(' ')[0]}
              </span>
              <span
                className="ml-2 inline-block text-3xl sm:text-4xl lg:text-5xl align-middle"
                style={{ animation: 'wave-hand 2.2s ease-in-out 0.6s 2', transformOrigin: '70% 70%' }}
                role="img" aria-label="wave">
                👋
              </span>
            </h1>
            {profile.interests?.length > 0 && (
              <p className="text-sm mt-3 opacity-70 max-w-xl leading-relaxed" style={{ color: 'var(--st-mute)' }}>
                <span className="font-semibold" style={{ color: 'var(--st-primary)' }}>Personalized</span> for {profile.interests.slice(0, 4).join(' · ')}
              </p>
            )}
          </div>
          <Button onClick={() => nav('/student/settings')} variant="ghost"
            className="rounded-full text-xs font-semibold hover:scale-105 transition-transform"
            data-testid="edit-profile-btn">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Edit profile
          </Button>
        </div>
        <style>{`
          @keyframes wave-hand {
            0%, 60%, 100% { transform: rotate(0deg); }
            10%, 30%, 50% { transform: rotate(14deg); }
            20%, 40% { transform: rotate(-8deg); }
          }
        `}</style>
      </section>

      {/* ── 2) Active Institution Hero ─────────────────────────────────── */}
      {activeTid && tenant ? (
        <ActiveHero tenant={tenant} mem={activeMem} credits={credits}
          classCount={classes.length} lessonCount={recentTopics.length}
          superCredits={superCredits} theme={theme} />
      ) : memberships.length === 0 ? (
        <EmptyJoinCard />
      ) : null}

      {/* ── 4) Pending Invitations ─────────────────────────────────────── */}
      {pendingTotal > 0 && (
        <section data-testid="pending-invites-section">
          <SectionHeader icon={<Inbox className="w-3.5 h-3.5" />} title="Pending Invitations" count={pendingTotal} />
          <div className="space-y-2">
            {pendingJoinRequests.map(jr => (
              <InviteRow key={jr.id} kind="join_request" row={jr}
                acting={busyId === jr.id}
                onAccept={() => acceptInvite(jr.id, jr.institution?.name || jr.institution?.tenant_name)}
                onDecline={() => declineInvite(jr.id)} />
            ))}
            {pendingInvitesPre.map(inv => (
              <InviteRow key={inv.id} kind="pre_signup_invite" row={inv}
                acting={false} hint="Sign-up link emailed to you — finish there to claim the licence." />
            ))}
            {/* Legacy /auth/my-join-requests for back-compat */}
            {requests.map(r => (
              <div key={r.id} className="rounded-2xl p-4 border-2 flex flex-wrap items-center gap-3"
                style={{ background: 'var(--st-card)', borderColor: 'var(--st-primary)' }}
                data-testid={`join-req-${r.id}`}>
                <Mail className="w-4 h-4 opacity-60" />
                <div className="flex-1 min-w-[200px]">
                  <div className="text-sm font-bold">{r.institution_name}</div>
                  <div className="text-[11px] opacity-60">Invited as {r.requested_role} · {r.licence_duration_months || 12} months</div>
                </div>
                <Button onClick={() => respondLegacy(r.id, 'decline')} disabled={busyId === r.id} variant="ghost" className="rounded-full text-xs" data-testid={`decline-${r.id}`}>
                  <X className="w-3.5 h-3.5 mr-1" /> Decline
                </Button>
                <Button onClick={() => respondLegacy(r.id, 'accept')} disabled={busyId === r.id} className="rounded-full text-xs text-white" style={{ background: 'var(--st-primary)' }} data-testid={`accept-${r.id}`}>
                  <Check className="w-3.5 h-3.5 mr-1" /> Accept & Join
                </Button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── 5) My Institutions grid (switch-here cards) ───────────────── */}
      {memberships.length > 0 && (
        <section data-testid="institutions-section">
          <SectionHeader icon={<Building2 className="w-3.5 h-3.5" />} title="My Institutions" count={memberships.length} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {memberships.map(m => (
              <InstitutionCard key={m.tenant_id} m={m}
                active={m.tenant_id === activeTid}
                switching={switching === m.tenant_id}
                onSwitch={() => switchTo(m.tenant_id)} />
            ))}
          </div>
        </section>
      )}

      {/* ── 6) My Classes (active institution) ─────────────────────────── */}
      {activeTid && tenant && (
        <section data-testid="classes-section">
          <SectionHeader icon={<BookOpen className="w-3.5 h-3.5" />} title="My Classes" count={classes.length} />
          {classes.length === 0 ? (
            <EmptyRow>You aren't enrolled in any classes yet at this institution.</EmptyRow>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {classes.map(c => (
                <button key={c.id} onClick={() => nav(`/student/class/${c.id}`)} data-testid={`class-card-${c.id}`}
                  className="text-left rounded-2xl p-4 border-2 hover:-translate-y-0.5 transition-all"
                  style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
                  <div className="text-[10px] font-bold uppercase tracking-wider opacity-60">{c.code}</div>
                  <div className="text-sm font-bold mt-1 truncate">{c.name}</div>
                  <div className="text-[11px] opacity-60 mt-1 line-clamp-1">{(c.subjects || []).join(' · ') || '—'}</div>
                  {c.latest_topic && (
                    <div className="mt-3 pt-3 border-t text-[11px]" style={{ borderColor: 'var(--st-border)' }}>
                      <span className="opacity-60">Latest:</span> <span className="font-semibold">{c.latest_topic.title}</span>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </section>
      )}

      {/* ── 7) Recently Published ──────────────────────────────────────── */}
      {activeTid && tenant && recentTopics.length > 0 && (
        <section data-testid="recent-topics-section">
          <SectionHeader icon={<Layers className="w-3.5 h-3.5" />} title="Recently Published" count={recentTopics.length} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {recentTopics.map(t => (
              <button key={t.id} onClick={() => nav(`/student/topic/${t.id}`)} data-testid={`topic-row-${t.id}`}
                className="text-left rounded-2xl p-4 border-2 flex items-center gap-3 hover:bg-black/5 transition-all"
                style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
                <div className="w-10 h-11 rounded-lg flex flex-col items-center justify-center text-white text-[10px] font-bold flex-shrink-0" style={{ background: 'var(--st-primary)' }}>
                  <Calendar className="w-3 h-3 mb-0.5" />
                  <span>{t.topic_date ? new Date(t.topic_date).toLocaleDateString('en', { month: 'short', day: 'numeric' }) : '—'}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold truncate">{t.title}</div>
                  <div className="text-[11px] opacity-60 truncate">{t.summary}</div>
                </div>
                <ArrowRight className="w-4 h-4 opacity-50" />
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Sub-components
// ════════════════════════════════════════════════════════════════════════

function ActiveHero({ tenant, mem, credits, classCount, lessonCount, superCredits, theme }) {
  // The hero's own gradient runs primary → accent, and the heading sits at
  // the top-left where `primary` dominates — so contrast is picked off that.
  const heroText = pickContrastColor(theme?.primary);
  const chipBg = heroText === '#FFFFFF' ? 'rgba(255,255,255,0.2)' : 'rgba(15,23,42,0.12)';
  return (
    <section data-testid="active-institution-hero">
      <div className="rounded-3xl p-6 sm:p-7 border-2 relative overflow-hidden"
        style={{
          borderColor: 'var(--st-border)',
          background: 'linear-gradient(135deg, var(--st-primary) 0%, var(--st-accent) 100%)',
        }}>
        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center gap-5" style={{ color: heroText }}>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-widest opacity-80">Active Institution</p>
            <h2 className="text-2xl sm:text-3xl font-extrabold mt-1 truncate" style={{ fontFamily: 'Space Grotesk, sans-serif', color: heroText }}>
              {tenant.name}
            </h2>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] opacity-95">
              <span className="px-2 py-0.5 rounded-full capitalize font-semibold" style={{ background: chipBg }}>{tenant.tenant_type || 'School'}</span>
              {mem?.department_name && <span className="px-2 py-0.5 rounded-full" style={{ background: chipBg }}>{mem.department_name}</span>}
              {tenant.institution_code && <span className="px-2 py-0.5 rounded-full font-mono" style={{ background: chipBg }}>{tenant.institution_code}</span>}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 text-[11px]">
            <Stat label="Classes" value={classCount} textColor={heroText} chipBg={chipBg} />
            <Stat label="Lessons" value={lessonCount} textColor={heroText} chipBg={chipBg} />
            <Stat label={`${(tenant.name || 'Inst').split(' ')[0]} Credits`} value={credits?.credits_remaining ?? 0} hint="per-institution" textColor={heroText} chipBg={chipBg} />
            <Stat label="Super Credits" value={superCredits} hint="global" highlight textColor={heroText} chipBg={chipBg} />
          </div>
        </div>
      </div>
    </section>
  );
}


function InstitutionCard({ m, active, switching, onSwitch }) {
  return (
    <div className={`rounded-2xl p-4 border-2 transition-all ${active ? '' : 'hover:-translate-y-0.5'}`}
      style={{
        borderColor: active ? 'var(--st-primary)' : 'var(--st-border)',
        background: 'var(--st-card)',
        boxShadow: active ? '0 0 0 3px color-mix(in srgb, var(--st-primary) 14%, transparent)' : 'none',
      }}
      data-testid={`institution-card-${m.tenant_id}`}>
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 text-white text-xs font-extrabold"
          style={{ background: 'var(--st-primary)' }}>
          {(m.tenant_name || '??').slice(0, 2).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold truncate flex items-center gap-2">
            {m.tenant_name}
            {active && <span className="text-[9px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded text-white" style={{ background: 'var(--st-primary)' }}>Active</span>}
          </div>
          <div className="text-[10px] opacity-60 mt-0.5 flex flex-wrap gap-1.5">
            {m.institution_code && <span className="font-mono">{m.institution_code}</span>}
            {m.department_name && <span>· {m.department_name}</span>}
            <span>· {m.role || 'Student'}</span>
          </div>
          <div className="mt-2 inline-flex items-center gap-1 text-[11px]" style={{ color: 'var(--st-mute)' }}>
            <ShieldCheck className="w-3 h-3" />
            <span><b>{m.credits_remaining ?? m.monthly_credits ?? 0}</b> credits here</span>
          </div>
        </div>
      </div>
      {!active && (
        <button onClick={onSwitch} disabled={switching}
          data-testid={`institution-card-switch-${m.tenant_id}`}
          className="mt-3 w-full rounded-lg px-3 py-1.5 text-[11px] font-bold inline-flex items-center justify-center gap-1.5 text-white disabled:opacity-50"
          style={{ background: 'var(--st-primary)' }}>
          {switching ? 'Switching…' : <>Switch here <ArrowRight className="w-3 h-3" /></>}
        </button>
      )}
    </div>
  );
}

function InviteRow({ kind, row, acting, hint, onAccept, onDecline }) {
  const inst = row.institution || {};
  return (
    <div className="rounded-2xl p-4 border-2 flex items-start gap-3"
      style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}
      data-testid={`invite-row-${row.id}`}>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: 'color-mix(in srgb, var(--st-primary) 14%, transparent)', color: 'var(--st-primary)' }}>
        <Mail className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold truncate">{inst.name || 'Institution'}</div>
        <div className="text-[11px] opacity-60 mt-0.5">
          As <span className="font-bold capitalize">{row.requested_role || row.role || 'Student'}</span>
          {row.licence_duration_months && <> · {row.licence_duration_months}mo licence</>}
          {row.invited_by_name && <> · by {row.invited_by_name}</>}
        </div>
        {hint && <div className="text-[11px] mt-1 opacity-70 italic">{hint}</div>}
      </div>
      {kind === 'join_request' && (
        <div className="flex flex-col gap-1.5 items-end">
          <button onClick={onAccept} disabled={acting}
            data-testid={`invite-accept-${row.id}`}
            className="rounded-lg px-3 py-1.5 text-[11px] font-bold text-white disabled:opacity-50"
            style={{ background: 'var(--st-primary)' }}>
            {acting ? '…' : 'Accept'}
          </button>
          <button onClick={onDecline} disabled={acting}
            data-testid={`invite-decline-${row.id}`}
            className="rounded-lg px-3 py-1.5 text-[11px] font-bold disabled:opacity-50"
            style={{ border: '1px solid var(--st-border)', color: 'var(--st-mute)' }}>
            Decline
          </button>
        </div>
      )}
    </div>
  );
}

function EmptyJoinCard() {
  return (
    <div className="rounded-3xl p-8 sm:p-10 border-2 text-center max-w-2xl"
      style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}
      data-testid="no-active-tenant">
      <div className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center" style={{ background: 'var(--st-primary)' }}>
        <GraduationCap className="w-8 h-8 text-white" />
      </div>
      <h3 className="text-xl font-extrabold mt-4">You're not in any institution yet</h3>
      <p className="text-sm opacity-70 mt-2 max-w-md mx-auto">
        Share your registered email <span className="font-bold opacity-100" style={{ color: 'var(--st-primary)' }}>(the one you used to sign up)</span> with your school or college admin —
        they'll send an invitation to this Desk. You can join unlimited institutions from one account.
      </p>
    </div>
  );
}

// ─── Tiny stateless atoms ─────────────────────────────────────────────────

function SectionHeader({ icon, title, count }) {
  return (
    <div className="mb-3 flex items-baseline gap-2">
      <h2 className="text-xs font-bold uppercase tracking-widest opacity-60 flex items-center gap-1.5">
        {icon} {title}
        {typeof count === 'number' && (
          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ background: 'var(--st-bg)', color: 'var(--st-mute)' }}>{count}</span>
        )}
      </h2>
    </div>
  );
}

function EmptyRow({ children }) {
  return (
    <div className="rounded-2xl p-5 border-2 text-sm opacity-60 text-center"
      style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
      {children}
    </div>
  );
}

function Stat({ label, value, hint, highlight, textColor = '#FFFFFF', chipBg = 'rgba(255,255,255,0.18)' }) {
  return (
    <div className="px-3 py-1.5 rounded-lg" style={{ background: chipBg, color: textColor, boxShadow: highlight ? `inset 0 0 0 2px ${textColor === '#FFFFFF' ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.25)'}` : 'none' }}>
      <div className="text-[10px] uppercase tracking-wider opacity-80">{label}</div>
      <div className="text-lg font-extrabold leading-tight">{value}</div>
      {hint && <div className="text-[9px] opacity-70 mt-0.5">{hint}</div>}
    </div>
  );
}
